/**
 * Timeline of an iQ -> Daton handoff, for diagnosing slow or failed product switches.
 *
 * Steps are timestamped from the first one, printed as `[iq-handoff]` console lines, kept in
 * sessionStorage (so a reload mid-handoff keeps the story), added as Sentry breadcrumbs, and
 * sent to Sentry as one message with the whole timeline when the handoff fails. `iq_trace` (set
 * by iQ in the handoff URL) joins this log with iQ's. In the console run
 * `__iqHandoffTrace()` to read the timeline.
 */

import * as Sentry from '@sentry/angular';
import { isIqSetupActive } from './iq-setup-context.helper';

export interface HandoffTraceStep {
  /** Milliseconds since the first step. */
  t: number;
  step: string;
  data?: Record<string, unknown>;
}

const STORAGE_KEY = 'iq_handoff_trace';
const MAX_STEPS = 60;
/** A trace older than this belongs to an earlier handoff; start a fresh one. */
const TRACE_TTL_MS = 10 * 60 * 1000;
/** Id prefix of a trace Daton started itself (iQ sent no `iq_trace`). */
const LOCAL_TRACE_PREFIX = 'daton-';

interface StoredTrace {
  id: string;
  startedAt: number;
  steps: HandoffTraceStep[];
}

let memoryTrace: StoredTrace | null = null;

function readStored(): StoredTrace | null {
  try {
    const raw = sessionStorage.getItem(STORAGE_KEY);
    return raw ? (JSON.parse(raw) as StoredTrace) : null;
  } catch {
    return null;
  }
}

function persist(trace: StoredTrace): void {
  memoryTrace = trace;
  try {
    sessionStorage.setItem(STORAGE_KEY, JSON.stringify(trace));
  } catch {
    // Private mode or quota: the in-memory copy still works for this page load.
  }
}

/** Trace id carried by iQ in the URL itself or inside `redirectTo`; null when absent. */
export function readTraceIdFromUrl(href: string = window.location.href): string | null {
  try {
    const url = new URL(href);
    const direct = url.searchParams.get('iq_trace');
    if (direct) {
      return direct;
    }
    const redirectTo = url.searchParams.get('redirectTo');
    return redirectTo
      ? new URL(redirectTo, url.origin).searchParams.get('iq_trace')
      : null;
  } catch {
    return null;
  }
}

function currentTrace(): StoredTrace {
  const fromUrl = readTraceIdFromUrl();
  const existing = memoryTrace ?? readStored();
  const fresh = !existing
    || Date.now() - existing.startedAt > TRACE_TTL_MS
    || (fromUrl !== null && fromUrl !== existing.id);
  if (!fresh && existing) {
    memoryTrace = existing;
    return existing;
  }
  const trace: StoredTrace = {
    id: fromUrl ?? `${LOCAL_TRACE_PREFIX}${Date.now().toString(36)}`,
    startedAt: Date.now(),
    steps: [],
  };
  persist(trace);
  return trace;
}

/**
 * True while an iQ handoff is in progress: iQ's `iq_trace` is in the URL (or its `redirectTo`),
 * a trace iQ started is still fresh, or the iQ setup context is active. Direct Daton logins and
 * other products' handoffs are not traced, so they add no console lines or Sentry messages.
 */
export function isHandoffTraceActive(): boolean {
  try {
    if (readTraceIdFromUrl() !== null || isIqSetupActive()) {
      return true;
    }
    const existing = memoryTrace ?? readStored();
    return !!existing
      && !existing.id.startsWith(LOCAL_TRACE_PREFIX)
      && Date.now() - existing.startedAt <= TRACE_TTL_MS;
  } catch {
    return false;
  }
}

/** Record one step of the handoff; a no-op outside one. Never throws. */
export function traceStep(step: string, data?: Record<string, unknown>): void {
  try {
    if (!isHandoffTraceActive()) {
      return;
    }
    const trace = currentTrace();
    const entry: HandoffTraceStep = { t: Date.now() - trace.startedAt, step, data };
    trace.steps = [...trace.steps, entry].slice(-MAX_STEPS);
    persist(trace);
    console.info(`[iq-handoff ${trace.id}] +${entry.t}ms ${step}`, data ?? '');
    Sentry.addBreadcrumb({
      category: 'iq-handoff',
      message: step,
      level: 'info',
      data: { traceId: trace.id, t: entry.t, ...data },
    });
  } catch {
    // Diagnostics must never break the handoff.
  }
}

/** Report a failed handoff to Sentry with the full timeline; a no-op outside a handoff. */
export function traceFailure(reason: string, data?: Record<string, unknown>): void {
  try {
    if (!isHandoffTraceActive()) {
      return;
    }
    traceStep(`FAILED: ${reason}`, data);
    const trace = currentTrace();
    Sentry.captureMessage(`iq handoff failed: ${reason}`, {
      level: 'warning',
      tags: { feature: 'iq-handoff', traceId: trace.id },
      extra: { timeline: trace.steps, ...data },
    });
  } catch {
    // Same as above.
  }
}

/** Forget the current trace (memory and sessionStorage); for tests. */
export function resetHandoffTrace(): void {
  memoryTrace = null;
  try {
    sessionStorage.removeItem(STORAGE_KEY);
  } catch {
    // Nothing to clear when storage is unavailable.
  }
}

/** Return the current in-memory or sessionStorage handoff trace, or null if none exists. */
export function getHandoffTrace(): StoredTrace | null {
  return memoryTrace ?? readStored();
}

/** Path only, so tokens in query strings never reach logs. */
export function traceUrlPath(url: string): string {
  try {
    return new URL(url, window.location.origin).pathname;
  } catch {
    return url.split('?')[0];
  }
}

if (typeof window !== 'undefined') {
  (window as unknown as Record<string, unknown>)['__iqHandoffTrace'] = getHandoffTrace;
}
